import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { getStats } from "@/lib/api";
import { FadeUp } from "@/components/motion/Reveal";

function useCountUp(target, active, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || !target) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

const fmt = (n) => Math.round(n).toLocaleString();

export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [stats, setStats] = useState({
    families_protected: 12480,
    avg_savings_pct: 22,
    quotes_generated: 38900,
    rating: 4.9,
  });

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
  }, []);

  const families = useCountUp(stats.families_protected, inView);
  const savings = useCountUp(stats.avg_savings_pct, inView);
  const quotes = useCountUp(stats.quotes_generated, inView);

  const items = [
    { value: `${fmt(families)}`, label: "Families protected" },
    { value: `${fmt(savings)}%`, label: "Average savings" },
    { value: `${fmt(quotes)}`, label: "Quotes generated" },
    { value: `${stats.rating}★`, label: "Member rating" },
  ];

  return (
    <section ref={ref} className="bg-cream py-16 lg:py-20" data-testid="stats-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
        {items.map((it, i) => (
          <FadeUp key={i} delay={i * 0.08} className="border-l-2 border-terracotta/40 pl-5">
            <p className="font-serif text-4xl lg:text-5xl text-forest leading-none" data-testid={`stat-value-${i}`}>
              {it.value}
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.16em] text-forest/55 font-semibold">{it.label}</p>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}
